import { Fragment, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ChevronDown,
  ChevronRight,
  Info,
  Pencil,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

import {
  useCreateOrgRoleMutation,
  useDeleteOrgRoleMutation,
  useOrgRoleCatalogQuery,
  useOrgRoleDetailQuery,
  useToggleOrgRolePermissionMutation,
  useUpdateOrgRoleMutation,
} from '@/client/api/admin/organizations';
import { usePermissionRegistryQuery } from '@/client/api/admin/permissions';
import type {
  AdminOrgRole,
  AdminPermission,
} from '@/client/types/admin/users-orgs';
import { getApiErrorMessage } from '@/client/utils/errors';

import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

const ORG_ROLES_PERMISSION = 'system.org_roles.manage';

const getApiErrorCode = (error: unknown): string | undefined => {
  const err = error as {
    data?: { error?: string; data?: { error?: string } };
    response?: { data?: { error?: string; data?: { error?: string } } };
  };
  const code =
    err?.data?.data?.error ??
    err?.data?.error ??
    err?.response?.data?.data?.error ??
    err?.response?.data?.error;
  return typeof code === 'string' ? code : undefined;
};

// ─── Scope banner ─────────────────────────────────────────────────────────────

const ScopeNotice = () => (
  <div className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
    <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" aria-hidden="true" />
    <p>
      These organization roles are a <strong>global</strong> catalogue (
      <code className="rounded bg-blue-100 px-1 py-0.5 text-xs">ourid</code>
      ) — they are <strong>not</strong> scoped to a single organization (
      <code className="rounded bg-blue-100 px-1 py-0.5 text-xs">oid</code>
      ). Editing a role such as &ldquo;manager&rdquo; changes it for{' '}
      <strong>every</strong> organization. Member assignment still happens on each
      organization&rsquo;s Members tab.
    </p>
  </div>
);

// ─── Create / edit dialog ─────────────────────────────────────────────────────

interface RoleDialogProps {
  open: boolean;
  /** `null` = create, otherwise edit that row. */
  role: AdminOrgRole | null;
  onClose: () => void;
}

const RoleDialog = ({ open, role, onClose }: RoleDialogProps) => {
  const isEdit = role !== null;
  const [form, setForm] = useState({
    role: role?.role ?? '',
    role_friendly: role?.role_friendly ?? '',
    description: role?.description ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateOrgRoleMutation();
  const updateMutation = useUpdateOrgRoleMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const systemRole = form.role.trim();
    const friendly = form.role_friendly.trim();
    if (!friendly) {
      setError('Friendly name is required.');
      return;
    }
    if (!isEdit && !systemRole) {
      setError('System role key is required.');
      return;
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          ourid: role.ourid,
          role_friendly: friendly,
          description: form.description.trim() || undefined,
        });
        toast.success(`Role "${friendly}" updated`);
      } else {
        await createMutation.mutateAsync({
          role: systemRole,
          role_friendly: friendly,
          description: form.description.trim() || undefined,
        });
        toast.success(`Role "${friendly}" created`);
      }
      onClose();
    } catch (err: unknown) {
      const code = getApiErrorCode(err);
      const status = (err as { status?: number })?.status;
      if (status === 409 || code === 'role_exists') {
        setError(`A role with the system key "${systemRole}" already exists.`);
        return;
      }
      toast.error(getApiErrorMessage(err, 'Failed to save the organization role.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            {isEdit ? 'Edit organization role' : 'Create organization role'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="org-role-key">
              System role <span className="text-red-500">*</span>
            </Label>
            <Input
              id="org-role-key"
              placeholder="e.g. manager"
              value={form.role}
              disabled={isEdit}
              onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
            />
            <p className="text-xs text-gray-500">
              {isEdit
                ? 'The system key is locked after create.'
                : 'Stable machine key — cannot be changed later.'}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="org-role-friendly">
              Friendly name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="org-role-friendly"
              placeholder="e.g. Manager"
              value={form.role_friendly}
              onChange={(e) =>
                setForm((p) => ({ ...p, role_friendly: e.target.value }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="org-role-desc">Description</Label>
            <Input
              id="org-role-desc"
              placeholder="What this role can do"
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create role'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Permission matrix ────────────────────────────────────────────────────────

interface PermissionMatrixProps {
  ourid: number;
  grantsBypassed: boolean;
  canManage: boolean;
  registry: AdminPermission[];
  registryLoading: boolean;
}

const PermissionMatrix = ({
  ourid,
  grantsBypassed,
  canManage,
  registry,
  registryLoading,
}: PermissionMatrixProps) => {
  const { data: detail, isLoading, isError } = useOrgRoleDetailQuery(ourid);
  const toggleMutation = useToggleOrgRolePermissionMutation();
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  const granted = useMemo(() => {
    const codes = new Set<string>();
    for (const perm of detail?.permissions ?? []) {
      if (perm.code) codes.add(perm.code);
    }
    return codes;
  }, [detail?.permissions]);

  const byCategory = useMemo(() => {
    const groups = new Map<string, AdminPermission[]>();
    for (const perm of registry) {
      const cat = perm.category || 'other';
      const list = groups.get(cat) ?? [];
      list.push(perm);
      groups.set(cat, list);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [registry]);

  const canToggle = canManage && !grantsBypassed;

  const handleToggle = async (code: string, currentlyGranted: boolean) => {
    if (!canToggle) return;
    setPendingCode(code);
    try {
      await toggleMutation.mutateAsync({
        ourid,
        code,
        grant: !currentlyGranted,
      });
      toast.success(currentlyGranted ? `Revoked ${code}` : `Granted ${code}`);
    } catch (err: unknown) {
      toast.error(
        getApiErrorMessage(err, 'Failed to update the permission grant.')
      );
    } finally {
      setPendingCode(null);
    }
  };

  if (isLoading || registryLoading) {
    return (
      <div className="space-y-2 px-6 py-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-5 w-full max-w-md" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="px-6 py-4 text-sm text-red-600">
        Could not load permissions for this role.
      </p>
    );
  }

  return (
    <div className="space-y-4 border-t border-blue-100 bg-blue-50/40 px-6 py-4">
      {grantsBypassed && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <Info
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
            aria-hidden="true"
          />
          <p>
            This is the owner role — permission grants are{' '}
            <strong>advisory only</strong> and do not affect runtime access.
            Toggles are disabled; saving would not change what owners can do.
          </p>
        </div>
      )}

      {!canManage && !grantsBypassed && (
        <p className="text-xs text-gray-500">
          Read-only — you need{' '}
          <code className="rounded bg-gray-100 px-1 py-0.5">
            {ORG_ROLES_PERMISSION}
          </code>{' '}
          to change grants.
        </p>
      )}

      {byCategory.length === 0 ? (
        <p className="text-sm text-gray-500">
          No org/store permissions in the registry.
        </p>
      ) : (
        byCategory.map(([category, perms]) => (
          <div key={category}>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              {category}
            </h4>
            <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
              {perms.map((perm) => {
                const isGranted = granted.has(perm.code);
                const busy = pendingCode === perm.code;
                return (
                  <label
                    key={perm.code}
                    className={`flex items-start gap-2 rounded-md border bg-white px-3 py-2 text-sm ${
                      canToggle
                        ? 'cursor-pointer hover:border-blue-200'
                        : 'cursor-default opacity-80'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={isGranted}
                      disabled={!canToggle || busy || toggleMutation.isPending}
                      onChange={() => handleToggle(perm.code, isGranted)}
                    />
                    <span>
                      <span className="font-medium text-gray-900">
                        {perm.name}
                      </span>
                      <span className="block font-mono text-xs text-gray-500">
                        {perm.code}
                      </span>
                      {perm.description && (
                        <span className="mt-0.5 block text-xs text-gray-400">
                          {perm.description}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const OrgRolesPage = () => {
  useBreadcrumbs([
    { label: 'Organizations', href: '/admin/organizations' },
    { label: 'Roles' },
  ]);

  const { hasPermission, isSuperAdmin } = useHasPermission(ORG_ROLES_PERMISSION);
  const canManage = hasPermission || isSuperAdmin;

  const [search, setSearch] = useState('');
  const [expandedOurid, setExpandedOurid] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminOrgRole | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminOrgRole | null>(null);

  const { data: roles = [], isLoading, isError } = useOrgRoleCatalogQuery();
  const { data: orgPerms = [], isLoading: orgPermsLoading } =
    usePermissionRegistryQuery({ category: 'org', per_page: 500 });
  const { data: storePerms = [], isLoading: storePermsLoading } =
    usePermissionRegistryQuery({ category: 'store', per_page: 500 });

  const registry = useMemo(() => {
    const seen = new Set<string>();
    const merged: AdminPermission[] = [];
    for (const perm of [...orgPerms, ...storePerms]) {
      if (seen.has(perm.code)) continue;
      seen.add(perm.code);
      merged.push(perm);
    }
    return merged.sort((a, b) => a.code.localeCompare(b.code));
  }, [orgPerms, storePerms]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) =>
        r.role_friendly?.toLowerCase().includes(q) ||
        r.role?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
    );
  }, [roles, search]);

  const deleteMutation = useDeleteOrgRoleMutation();

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync({ ourid: pendingDelete.ourid });
      toast.success(`Role "${pendingDelete.role_friendly}" deleted`);
      if (expandedOurid === pendingDelete.ourid) setExpandedOurid(null);
      setPendingDelete(null);
    } catch (err: unknown) {
      const code = getApiErrorCode(err);
      const status = (err as { status?: number })?.status;
      if (status === 403 || code === 'role_protected') {
        toast.error(
          `"${pendingDelete.role_friendly}" is protected and cannot be deleted.`
        );
        return;
      }
      if (status === 409 || code === 'role_in_use') {
        toast.error(
          `"${pendingDelete.role_friendly}" is still in use` +
            ` (${pendingDelete.assigned_members ?? 0} members,` +
            ` ${pendingDelete.permission_count ?? 0} permissions).` +
            ` Reassign members and clear grants before deleting.`
        );
        return;
      }
      toast.error(
        getApiErrorMessage(err, 'Failed to delete the organization role.')
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <div className="mb-6">
          <div className="mb-1 flex items-center gap-3">
            <ShieldCheck className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">
              Organization roles
            </h1>
          </div>
          <p className="ml-10 text-sm text-gray-500">
            Global role catalogue and permission grants shared by every
            organization
          </p>
        </div>

        <div className="mb-4">
          <ScopeNotice />
        </div>

        {!canManage && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            You do not have the{' '}
            <code className="rounded bg-amber-100 px-1.5 py-0.5 text-xs">
              {ORG_ROLES_PERMISSION}
            </code>{' '}
            permission, so this page is read-only. Ask an administrator to grant
            it if you need to change roles or grants.
          </div>
        )}

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search roles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canManage && (
            <div className="sm:ml-auto">
              <Button
                className="gap-2"
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
                Create role
              </Button>
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-4 py-3" />
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Friendly name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    System role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Members
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoading &&
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!isLoading && isError && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-sm text-red-600"
                    >
                      Could not load organization roles.
                    </td>
                  </tr>
                )}

                {!isLoading && !isError && filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-12 text-center text-sm text-gray-500"
                    >
                      {search.trim()
                        ? 'No roles match that search.'
                        : 'No organization roles defined yet.'}
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  !isError &&
                  filtered.map((row) => {
                    const expanded = expandedOurid === row.ourid;
                    return (
                      <Fragment key={row.ourid}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-4">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedOurid(expanded ? null : row.ourid)
                              }
                              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                              title={
                                expanded
                                  ? 'Collapse permissions'
                                  : 'Expand permissions'
                              }
                              aria-expanded={expanded}
                            >
                              {expanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {row.role_friendly}
                              </span>
                              {row.grants_bypassed && (
                                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                                  Grants bypassed
                                </span>
                              )}
                            </div>
                            {row.description && (
                              <p className="mt-0.5 max-w-xs truncate text-xs text-gray-500">
                                {row.description}
                              </p>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs text-gray-800">
                              {row.role}
                            </code>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                            {row.permission_count ?? '—'}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                            {row.assigned_members ?? '—'}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right">
                            {canManage ? (
                              <div className="flex items-center justify-end gap-3">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditing(row);
                                    setDialogOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                                  title="Edit role"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPendingDelete(row)}
                                  className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-800"
                                  title="Delete role"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">
                                Read-only
                              </span>
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td colSpan={6} className="p-0">
                              <PermissionMatrix
                                ourid={row.ourid}
                                grantsBypassed={Boolean(row.grants_bypassed)}
                                canManage={canManage}
                                registry={registry}
                                registryLoading={
                                  orgPermsLoading || storePermsLoading
                                }
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {dialogOpen && (
        <RoleDialog
          key={editing?.ourid ?? 'create'}
          open={dialogOpen}
          role={editing}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      )}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this organization role?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  "{pendingDelete?.role_friendly}" (
                  <code className="text-xs">{pendingDelete?.role}</code>) will be
                  removed from the global catalogue. This affects every
                  organization.
                </p>
                {(pendingDelete?.assigned_members ?? 0) > 0 ||
                (pendingDelete?.permission_count ?? 0) > 0 ? (
                  <p>
                    Currently:{' '}
                    <strong>{pendingDelete?.assigned_members ?? 0}</strong>{' '}
                    assigned members,{' '}
                    <strong>{pendingDelete?.permission_count ?? 0}</strong>{' '}
                    permission grants. Delete may fail with{' '}
                    <code className="text-xs">role_in_use</code> if either is
                    non-zero.
                  </p>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete role'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrgRolesPage;
